import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import Img from '../Img';
import PopupV3 from '../Popup/PopupV3';
import useClickOutsideRef from '../hooks/useClickOutsideRef';

import styles from './infoButton.scss';

function InfoButton({ fixedWidth, anchorClass, children, hoverText, small, translateXValue, alignRight: propAlignRight, 'data-testid': dataTestId }) {
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [displayMenuBelowAnchor, setDisplayMenuBelowAnchor] = useState(true);
    const [hoverWidth, setHoverWidth] = useState('300px');
    const [alignRight, setAlignRight] = useState(false);
    const [transformValue, setTransformValue] = useState('translateX(-50%)');

    const wrapperRef = useRef(null);

    const closePopUp = () => {
        setIsMenuVisible(false);
    };

    useClickOutsideRef(wrapperRef, () => setIsMenuVisible(false));

    const handleAchorClick = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();

        let width = 300;
        if (window.innerWidth > 500) {
            width = 400;
        }

        let right = false;
        let transform = 'translateX(-50%)';
        let distanceFromBorder = ev.nativeEvent.clientX;

        if (ev.nativeEvent.clientX > window.innerWidth / 2) {
            right = true;
            distanceFromBorder = window.innerWidth - ev.nativeEvent.clientX;
            transform = 'translateX(50%)';
        }

        if (distanceFromBorder < width / 2) {
            transform = right
                ? `translateX(${distanceFromBorder}px)`
                : `translateX(${-distanceFromBorder}px)`;
        }

        if (translateXValue) {
            transform = `translateX(${translateXValue})`;
        }

        const showBelow = ev.nativeEvent.clientY <= (2 * window.innerHeight / 3);

        if (fixedWidth) {
            width = fixedWidth;
        }

        setIsMenuVisible(true);
        setDisplayMenuBelowAnchor(showBelow);
        setHoverWidth(`${width}px`);
        setAlignRight(right);
        setTransformValue(transform);
    };

    let style;
    if (propAlignRight) {
        style = { width: hoverWidth, right: '0' };
    } else if (alignRight) {
        style = { width: hoverWidth, right: '15px', transform: transformValue };
    } else {
        style = { width: hoverWidth, left: '30px', transform: transformValue };
    }

    return (
        <div className={`${styles.popOverMenuRoot} ${children ? '' : styles.ellipsis}`} onClick={(e) => e.stopPropagation()} data-testid={dataTestId}>
            <div
                className={`${styles.popOverMenuAnchor} ${anchorClass || ''}`}
                onClick={handleAchorClick}
                data-testid={dataTestId && `${dataTestId}.anchor`}
            >
                {children || (
                    <Img
                        alt="tooltip"
                        src="/img/info-icon.svg"
                        className={`${styles.infoIcon} ${small ? styles.small : ''}`}
                        data-testid={dataTestId && `${dataTestId}.icon`}
                    />
                )}
            </div>
            {isMenuVisible && <div className={styles.anchorClickDisabler} />}
            <div
                className={`${styles.menuContainer} ${displayMenuBelowAnchor ? styles.belowAnchor : styles.aboveAnchor}`}
                style={style}
                ref={wrapperRef}
            >
                {isMenuVisible && (
                    <div className={styles.popOverMenu} onClick={(e) => e.stopPropagation()} data-testid={dataTestId && `${dataTestId}.popup`}>
                        <PopupV3
                            message={hoverText}
                            onConfirmClick={closePopUp}
                            isOpen={true}
                            noOverlay
                            showConfirmButton
                            confirmButtonLabel="Close"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

InfoButton.propTypes = {
    fixedWidth: PropTypes.number,
    anchorClass: PropTypes.string,
    children: PropTypes.node,
    hoverText: PropTypes.string,
    small: PropTypes.bool,
    translateXValue: PropTypes.string,
    alignRight: PropTypes.bool,
    'data-testid': PropTypes.string,
};

InfoButton.defaultProps = {
    fixedWidth: 0,
    anchorClass: '',
    children: null,
    hoverText: '',
    small: false,
    translateXValue: '',
    alignRight: false,
    'data-testid': undefined,
};

export default InfoButton;
